<script setup lang="ts">
import StackLayout from '@/components/Layouts/StackLayout.vue'
import PermissionWrapper from '@/components/Permission/PermissionWrapper.vue'
import { buildDetectionArray } from '@/socket/socketUtils'
import store from '@/store'
import { ECheckpointActions } from '@/store/checkpointStore/CheckpointStoreTypes'
import { EDetectionActions } from '@/store/detectionStore/DetectionStoreTypes'
import { EStoreModules } from '@/store/storeType'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import type { DetectionModel } from '@/types/models/DetectionModel'
import type { PartnerModel } from '@/types/partners/PartnerModel'
import { ECheckpointsPerm } from '@/types/permissions/CheckpointsPermissionsEnum'
import type { SocketCheckpointDetectionModel } from '@/types/socket/SocketDetectionModel'
import CheckpointDetailView from '@/views/CheckpointsView/CheckpointDetailView.vue'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { RdrChannelKey } from '../../../types/enum/RdrChannelEnum'
import CheckpointCardMode from './CheckpointsTable/CheckpointCardMode.vue'
import CheckpointRaceSelector from './CheckpointsTable/components/CheckpointRaceSelector.vue'
import EventCreateCheckpoint from './CreateCheckpoint/EventCreateCheckpoint.vue'
import EventSyncCheckpoint from './components/EventSyncCheckpoint.vue'

type Props = {
    sync?: boolean
    currentTabIndex: number
    partnerConfig?: PartnerModel
    detections: SocketCheckpointDetectionModel[]
    races?: string[]
}
const props = defineProps<Props>()
type Emits = {
    (e: 'refresh'): void
    (e: 'goTo', value?: CheckpointModel): void
    (e: 'checkpointUpdate', value?: SocketCheckpointDetectionModel[]): void
    (e: 'refreshDevices', associates: number[]): void
}
const emit = defineEmits<Emits>()

const route = useRoute()
const router = useRouter()
const eventId = computed(() => parseInt(route.params.event_id.toString()))

const checkpoint = ref<CheckpointModel>()
const checkpointId = computed(() =>
    route.query.checkpoint_id
        ? parseInt(route.query.checkpoint_id!.toString())
        : checkpoint.value?.id
)

const data = computed(() => store.state.checkpoint)
const latestDetections = ref<SocketCheckpointDetectionModel[]>([])
watch(
    () => props.detections,
    newDetection => (latestDetections.value = newDetection)
)

const getCheckpoints = () =>
    store
        .dispatch(`${EStoreModules.CHECKPOINT}/${ECheckpointActions.FETCH_CHECKPOINTS}`, {
            id: eventId.value,
            params: {
                per_page: -1,
                device_configuration: 1,
                device_configuration_filter: [RdrChannelKey]
            }
        })
        .then((res: CheckpointModel[]) => {
            latestDetections.value = res.map(item => ({
                checkpoint_id: item.id,
                detections: []
            }))
            if (checkpoint.value)
                checkpoint.value = res.find(item => item.id === checkpoint.value?.id)
            store
                .dispatch(`${EStoreModules.DETECTION}/${EDetectionActions.GET_LATEST_DETECTIONS}`, {
                    checkpoint_ids: res.map(item => item.id)
                })
                .then((resDetections: DetectionModel[]) =>
                    emit(
                        'checkpointUpdate',
                        buildDetectionArray(resDetections, latestDetections.value, 'checkpoint_id')
                    )
                )
            emit(
                'refreshDevices',
                res.flatMap(
                    item =>
                        item.associates?.flatMap(associatedDevice => associatedDevice.device_id) ??
                        []
                ) ?? []
            )
        })

onMounted(() => {
    getCheckpoints()
})
const handleDeleteCheckpoint = (value: CheckpointModel) =>
    store
        .dispatch(`${EStoreModules.CHECKPOINT}/${ECheckpointActions.DELETE_CHECKPOINT}`, {
            eventId: eventId.value,
            checkpointId: value.id,
            name: value.name
        })
        .then(() => getCheckpoints())

const raceFilter = ref<string>()
const filteredCheckpointsWithRace = computed(() =>
    props.races && raceFilter.value
        ? data.value?.data?.checkpoints?.filter(
              item =>
                  Array.isArray(item.partner_config?.race)
                      ? (item.partner_config?.race as string[]).includes(raceFilter.value!)
                      : item.partner_config?.race === raceFilter.value
          )
        : undefined
)

const handleSelect = (value?: string) => {
    router.replace({ query: { ...route.query, checkpoint_id: undefined } })
    raceFilter.value = value
}
</script>

<template>
    <StackLayout direction="column" :gap="8">
        <StackLayout direction="column" :gap="8" class="full-width flex-wrap">
            <StackLayout :class="races ? 'space-between' : 'justify-end'">
                <CheckpointRaceSelector
                    v-if="races"
                    :races="races"
                    :raceFilter="raceFilter"
                    @changeRaceFilter="handleSelect"
                    clearable />
                <PermissionWrapper :permission="ECheckpointsPerm.CHECKPOINTS_EDIT">
                    <EventSyncCheckpoint
                        v-if="sync"
                        :eventId="eventId"
                        :loading="!!store.state.checkpoint.isLoading"
                        @refresh="getCheckpoints" />
                    <EventCreateCheckpoint
                        v-else
                        :eventId="eventId"
                        :partnerConfig="partnerConfig"
                        :loading="!!store.state.checkpoint.isLoading"
                        @refresh="getCheckpoints" />
                </PermissionWrapper>
            </StackLayout>
            <CheckpointCardMode
                :currentTabIndex="currentTabIndex"
                :data="filteredCheckpointsWithRace ?? data.data.checkpoints"
                :detections="latestDetections"
                :loading="data.isLoading ?? false"
                :eventId="eventId"
                :sync="sync"
                :editPermission="ECheckpointsPerm.CHECKPOINTS_EDIT"
                :deletePermission="ECheckpointsPerm.CHECKPOINTS_DELETE"
                @delete="handleDeleteCheckpoint"
                @refresh="getCheckpoints" />
        </StackLayout>
        <CheckpointDetailView
            v-if="checkpointId"
            :checkpointId="checkpointId"
            :sync="sync"
            :partnerConfig="partnerConfig"
            @refresh="getCheckpoints" />
    </StackLayout>
</template>
